import React, { useState } from 'react';
import { Ban, Eye, EyeOff, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlockUserDialog = ({ 
  userId,
  username,
  trigger, // Custom trigger element
  onBlocked // Callback after successful block
}) => {
  const { user, token } = useAuth();
  const [open, setOpen] = useState(false);
  const [visibility, setVisibility] = useState('hide_interactions');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/moderation/blocks`,
        {
          blocked_user_id: userId,
          visibility: visibility,
          reason: reason || null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success(`${username || 'User'} has been blocked`);
      setOpen(false);
      setReason('');
      
      if (onBlocked) {
        onBlocked();
      }
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'User is already blocked');
      } else {
        toast.error('Failed to block user. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!user || user.id === userId) {
    return null; // Don't show block button for guests or for yourself
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive">
            <Ban className="w-4 h-4 mr-1" />
            Block
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ban className="w-5 h-5 text-destructive" />
            Block {username || 'this user'}
          </DialogTitle>
          <DialogDescription>
            Blocked users won't be able to interact with you. Choose how you want to handle their content.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-3">
            <Label>Block visibility settings</Label>
            <RadioGroup value={visibility} onValueChange={setVisibility}>
              <div
                className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer"
                onClick={() => setVisibility('hide_interactions')}
              >
                <RadioGroupItem value="hide_interactions" id="hide_interactions" className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor="hide_interactions" className="font-medium cursor-pointer flex items-center gap-2">
                    <EyeOff className="w-4 h-4" />
                    Prevent interactions only
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    They can't message you, comment on your posts, or send friend requests. You'll still see their public content.
                  </p>
                </div>
              </div>
              <div
                className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer"
                onClick={() => setVisibility('hide_completely')}
              >
                <RadioGroupItem value="hide_completely" id="hide_completely" className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor="hide_completely" className="font-medium cursor-pointer flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    Hide completely
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    All their content will be hidden from your feed. They won't be able to see your profile either.
                  </p>
                </div>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Reason (optional, for your records)</Label>
            <Textarea
              id="reason"
              placeholder="Why are you blocking this user?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              maxLength={500}
            />
          </div>

          <div className="bg-muted/50 p-3 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-muted-foreground mt-0.5" />
            <p className="text-sm text-muted-foreground">
              This will also remove any existing friendship with this user. You can unblock them at any time from your settings.
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="destructive" disabled={loading}>
              {loading ? 'Blocking...' : 'Block User'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Simple unblock confirmation dialog
export const UnblockConfirmDialog = ({ blockId, username, onUnblocked }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleUnblock = async () => {
    setLoading(true);
    try {
      await axios.delete(
        `${API_URL}/api/moderation/blocks/${blockId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success(`${username || 'User'} has been unblocked`);
      if (onUnblocked) {
        onUnblocked();
      }
    } catch (error) {
      toast.error('Failed to unblock user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" size="sm">
          Unblock
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Unblock {username || 'this user'}?</AlertDialogTitle>
          <AlertDialogDescription>
            They will be able to see your profile and interact with your content again.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleUnblock} disabled={loading}>
            {loading ? 'Unblocking...' : 'Unblock'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default BlockUserDialog;
